// Library methods

// Utils
import { toast } from 'react-toastify';
import { takeLatest, put, call } from 'redux-saga/effects';
import moment from 'moment/moment';
import { isEmpty } from 'lodash';
import { GET, POST } from '../../../utils/fetchWrappers';

// API URLs
import {
    API_CREATE_PAYMENT_PAGE,
    API_GET_PAYMENT_PAGE_DETAILS,
    API_UPDATE_PAYMENT_PAGE_DETAILS,
    API_LIST_PAY_TRANSACTIONS_PAYMENT_PAGE,
    FETCH_PAYMENT_PAGE_DATA,
    API_GENERATE_REPORT,
} from '../../../utils/API/apiUrls';

// Constants
import { apiStatusCodes } from '../../../utils/API/apiStatusCodes';

// Redux Actions
import * as loaderActions from '../../../redux/common/loader/loader.actions';
import {
    CREATE_PAYMENT_PAGE_REQUEST,
    GET_PAYMENT_PAGE_DETAILS_REQUEST,
    UPDATE_PAYMENT_PAGE_DETAILS_REQUEST,
    FETCH_PAYMENT_PAGE_REQUEST,
    FETCH_TRANSACTION_PAYMENT_REQUEST,
    VIEW_PAY_LINK_REQUEST,
    GENERATE_REPORT_REQUEST_PAYMENT_PAGE,
} from './paymentPage.constants';

import * as paymentPageActions from './paymentPage.actions';

const getRequestDataFile = payload => {
    const request = {};

    if (payload.createdOn) {
        request.startDateTime = moment(payload.createdOn.startDate)
            .startOf('day')
            .format('YYYY-MM-DD HH:mm:ss');
        request.endDateTime = moment(payload.createdOn.endDate)
            .endOf('day')
            .format('YYYY-MM-DD HH:mm:ss');
    }
    if (payload.reportFileName) {
        request.reportFileName = payload.reportFileName;
    }
    if (payload.linkDataId) {
        request.linkId = payload.linkDataId;
    }
    if (payload.transactionStatus) {
        request.transactionStates = [payload.transactionStatus];
    }
    if (payload.transactionId) {
        request.transactionId = payload.transactionId;
    }
    request.reportType = 'FORM_LINK';
    return request;
};

// Format Payment link table filter and Request params.
const getRequestData = payload => {
    const filter = {};
    const request = {
        paginationDetails: {
            pageNo: payload.pageIndex || 1,
            pageSize: payload.pageSize || 10,
        },
    };
    if (payload.createdOn) {
        filter.startDateTime = String(
            moment(payload.createdOn.startDate)
                .startOf('day')
                .valueOf()
        );
        filter.endDateTime = String(
            moment(payload.createdOn.endDate)
                .endOf('day')
                .valueOf()
        );
    }
    if (payload.pageId) {
        const { pageId } = payload;
        filter.dataId = pageId;
    }
    if (payload.status) {
        const { status } = payload;
        filter.formDataStatus = status;
    }
    // filter.dataId = 'PF5002';
    if (filter && !isEmpty(filter)) {
        request.filter = filter;
    }

    return request;
};

// Format Transaction View Payment link filter and Request params.
const getRequestDataTrxn = payload => {
    const filter = {};
    const request = {
        paginationDetails: {
            pageNo: payload.pageIndex || 1,
            pageSize: payload.pageSize || 10,
        },
    };

    if (payload.createdOn) {
        filter.startDateTime = String(
            moment(payload.createdOn.startDate)
                .startOf('day')
                .valueOf()
        );
        filter.endDateTime = String(
            moment(payload.createdOn.endDate)
                .endOf('day')
                .valueOf()
        );
    }
    if (payload.transactionId) {
        filter.transactionId = payload.transactionId;
    }
    if (payload.transactionStatus) {
        const { transactionStatus } = payload;
        filter.transactionStates = [transactionStatus];
    }
    if (payload.linkDataId) {
        filter.linkId = payload.linkDataId;
    }

    if (filter && !isEmpty(filter)) {
        request.filter = filter;
    }
    return request;
};

// Get details for the Payment link list for table.
export function* fetchPaymentPageDetails(actions) {
    yield put(paymentPageActions.showLoader());
    try {
        const { payload } = actions;
        const request = getRequestData(payload);
        let data = null;

        const res = yield call(POST, FETCH_PAYMENT_PAGE_DATA, request);
        data = res.data;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(paymentPageActions.fetchPaymentPageSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                paymentPageActions.fetchPaymentPageError({
                    error: data,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        const errorMessage = data.errorMessage;
        yield put(
            paymentPageActions.fetchPaymentPageError({
                error: data.errorMessage || '',
            })
        );
        if (errorMessage) {
            toast.error(errorMessage);
        }
    }
    yield put(paymentPageActions.hideLoader());
}

// Create new payment page Saga function
export function* createPaymentPageDetails(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload, handleSuccessClick, status, history } = actions;
        const response = yield call(POST, API_CREATE_PAYMENT_PAGE, payload);
        const data = response.data;
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(paymentPageActions.createPaymentPageSuccess(data));
            toast.success('Request Initiated successfully.');
            if (status === 'ACTIVE') {
                handleSuccessClick(data);
            } else {
                history.push('/payment-page');
            }
        } else if (data && data.errorMessage) {
            const errorMessage = data.errorMessage;
            yield put(
                paymentPageActions.createPaymentPageError({
                    error: data.errorMessage || '  ',
                })
            );
            if (errorMessage) {
                toast.error(errorMessage);
            }
        }
    } catch ({ response }) {
        const { data } = response;
        const errorMessage = data.errorMessage || data.error;
        yield put(
            paymentPageActions.createPaymentPageError({
                error: data.errorMessage || data.error || '',
            })
        );
        if (errorMessage) {
            toast.error(errorMessage);
        }
    }
    yield put(loaderActions.hideLoader());
}

export function* fetchPaymentPageEditDetails(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload } = actions;
        const response = yield call(POST, API_GET_PAYMENT_PAGE_DETAILS, payload);
        const data = response.data;
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(paymentPageActions.getPaymentPageDetailsSuccess(data));
        } else if (data && data.errorMessage) {
            const errorMessage = data.errorMessage;
            yield put(
                paymentPageActions.getPaymentPageDetailsError({
                    error: data.errorMessage || '',
                })
            );
            if (errorMessage) {
                toast.error(errorMessage);
            }
        }
    } catch ({ response }) {
        const { data } = response;
        const errorMessage = data.errorMessage;
        yield put(
            paymentPageActions.createPaymentPageError({
                error: data.errorMessage || '',
            })
        );
        if (errorMessage) {
            toast.error(errorMessage);
        }
    }
    yield put(loaderActions.hideLoader());
}

export function* updatePaymentPageDetails(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload, handleSuccessClick, status, history } = actions;
        const response = yield call(POST, API_UPDATE_PAYMENT_PAGE_DETAILS, payload);
        const data = response.data;
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(paymentPageActions.updatePaymentPageDetailsSuccess(data));
            toast.success('Request Updated successfully.');
            if (status === 'ACTIVE') {
                actions.handleSuccessClick(data);
            } else {
                history.push('/payment-page');
            }
        } else if (data && data.errorMessage) {
            const errorMessage = data.errorMessage;
            yield put(
                paymentPageActions.updatePaymentPageDetailsError({
                    error: data.errorMessage || '',
                })
            );
            if (errorMessage) {
                toast.error(errorMessage);
            }
        } else {
        }
    } catch ({ response }) {
        const { data } = response;
        const errorMessage = data.errorMessage;
        yield put(
            paymentPageActions.updatePaymentPageDetailsError({
                error: data.errorMessage || '',
            })
        );
        if (errorMessage) {
            toast.error(errorMessage);
        }
    }
    yield put(loaderActions.hideLoader());
}
// Get Details for the payment link transaction view.
export function* fetchPaymentsDetails(actions) {
    yield put(paymentPageActions.showLoader());
    try {
        const { payload } = actions;
        const request = getRequestDataTrxn(payload);
        let data = {};

        const res = yield call(POST, API_LIST_PAY_TRANSACTIONS_PAYMENT_PAGE, request);
        data = res.data;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(paymentPageActions.fetchPaymentsSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                paymentPageActions.fetchPaymentsError({
                    error: data,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        const errorMessage = data.errorMessage;
        yield put(
            paymentPageActions.fetchPaymentsError({
                error: data.errorMessage || '',
            })
        );
        if (errorMessage) {
            toast.error(errorMessage);
        }
    }
    yield put(paymentPageActions.hideLoader());
}

// View the payment link summary Saga function
export function* viewPayLinkDetails(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload } = actions;
        const request = {
            filter: {
                linkId: payload.linkDataId,
            },
            paginationDetails: {
                pageNo: 1,
                pageSize: 10,
            },
        };
        let data = {};

        const res = yield call(POST, FETCH_PAYMENT_PAGE_DATA, request);
        data = res.data;

        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(paymentPageActions.viewPayLinksSuccess(data));
        } else if (data && data.errorMessage) {
            yield put(
                paymentPageActions.viewPayLinksError({
                    error: data,
                })
            );
        }
    } catch ({ response }) {
        const { data } = response;
        const errorMessage = data.errorMessage;
        yield put(
            paymentPageActions.viewPayLinksError({
                error: data.errorMessage || '',
            })
        );
        if (errorMessage) {
            toast.error(errorMessage);
        }
    }
    yield put(loaderActions.hideLoader());
}

export function* generateTransactionReport(actions) {
    yield put(loaderActions.showLoader());
    try {
        const { payload } = actions;

        const handleViewDownloads = payload.handleViewDownloads;
        delete payload.handleViewDownloads;
        const request = getRequestDataFile(payload);

        const { data } = yield call(POST, API_GENERATE_REPORT, request);
        if (data && data.statusCode === apiStatusCodes.SUCCESS) {
            yield put(paymentPageActions.generateReportSuccess(data));
            handleViewDownloads();
        } else if (data && data.errorMessage) {
            const errorMessage = data.errorMessage || '';
            yield put(
                paymentPageActions.generateReportError({
                    error: errorMessage || '',
                })
            );
            if (errorMessage) {
                toast.error(errorMessage);
            }
        }
    } catch ({ response }) {
        const { data } = response;
        const errorMessage = data.errorMessage || '';
        yield put(
            paymentPageActions.generateReportError({
                error: errorMessage || '',
            })
        );
        if (errorMessage) {
            toast.error(errorMessage);
        }
    }
    yield put(loaderActions.hideLoader());
}

export function* paymentPageSagas() {
    yield takeLatest(FETCH_PAYMENT_PAGE_REQUEST, fetchPaymentPageDetails);
    yield takeLatest(CREATE_PAYMENT_PAGE_REQUEST, createPaymentPageDetails);
    yield takeLatest(GET_PAYMENT_PAGE_DETAILS_REQUEST, fetchPaymentPageEditDetails);
    yield takeLatest(UPDATE_PAYMENT_PAGE_DETAILS_REQUEST, updatePaymentPageDetails);
    yield takeLatest(GENERATE_REPORT_REQUEST_PAYMENT_PAGE, generateTransactionReport);
    yield takeLatest(FETCH_TRANSACTION_PAYMENT_REQUEST, fetchPaymentsDetails);
    yield takeLatest(VIEW_PAY_LINK_REQUEST, viewPayLinkDetails);
}
